import React from 'react';
import DescriptionItem from './DescriptionItem';
import renderHtml from '../../utils/renderHtml';
import DocumentList from './DocumentList';
import { DEFAULT_APPLICATION_TEXT, LANGUAGE } from '../../constants/constants';
import { IInterventionDetailShowProps } from '../../interfaces/InterventionDetailProps';
import UnorderedListOfObjectText from './UnorderedListOfObjectText';
import ContactList from './ContactList';
import { Bijlage } from '../../interfaces/intervention-api/interventionDetails';
import ExternalLinkList from './ExternalLinkList';

const ExpandedInterventionDetail = (props: IInterventionDetailShowProps): JSX.Element | null => {
  const {
    iId,
    interventionDetail,
  } = props;

  const {
    DETAIL_DESCRIPTION_TITLE_SHORT,
    YES_TEXT,
    NO_TEXT,
  } = DEFAULT_APPLICATION_TEXT[LANGUAGE];

  /**
   * A Function that generates the "lifetime from and lifetime to" text
   * @return {string}
   */
  const getAgeText = () => (
    `${interventionDetail.interventieLeeftijdGeslacht.leeftijdMin} t/m ${interventionDetail.interventieLeeftijdGeslacht.leeftijdMax}`
  );

  /**
   * A method to generate "Yes" / "No" text based on a boolean value being passed
   * @param bool
   * @return {string}
   */
  const getYesNoText = (bool: boolean | undefined) => (bool ? YES_TEXT : NO_TEXT);

  /**
   * A method to generate an un-ordered list of document download links, if there are documents to show
   * @param documents
   * @return {JSX.Element | null}
   */
  const getInterventionDocuments = (documents: Bijlage[]) => (
    documents && documents.length > 0
      ? <DocumentList documents={documents} />
      : null
  );

  /**
   * A method to generate a list of un-ordered list items if there are items to show
   * @param listItems
   * @return {JSX.Element | null}
   */
  const getUnOrderedListItems = (listItems: any[]) => (
    listItems && listItems.length > 0
      ? <UnorderedListOfObjectText objectList={listItems} />
      : null
  );

  /**
   * A method to only generate external links JSX if there are external links to show
   * @param externalLinks
   * @return {JSX.Element | null}
   */
  const getExternalLinks = (externalLinks: any[]) => (
    externalLinks && externalLinks.length > 0
      ? <ExternalLinkList externalLinks={externalLinks} />
      : null
  );

  /**
   * A method to generate the required HTML string or to return null
   * @param detail - from the interventions database
   * @return {ReactElement<any, string | JSXElementConstructor<any>> | null}
   */
  const getRenderedHtml = (detail: string) => (
    detail
      ? renderHtml(detail)
      : null
  );

  return (
    <div data-testid={iId}>
      <div className="d-flex">
        <div className="p-2">
          <h2 className="c-intervention__title">{interventionDetail?.naam}</h2>
        </div>
      </div>
      <div className="c-intervention-description d-flex flex-column">
        <div className="c-intervention-description__group">
          <div className="c-intervention-description__header">
            <h3 className="c-intervention-description__title">{ DETAIL_DESCRIPTION_TITLE_SHORT }</h3>
          </div>
          <dl className="d-flex flex-wrap">
            <DescriptionItem
              term="Doel"
              description={getRenderedHtml(interventionDetail?.interventieAlgemeen.doel)}
            />
            <DescriptionItem
              term="Korte omschrijving"
              description={getRenderedHtml(interventionDetail?.interventieAlgemeen.omschrijving)}
            />
            <DescriptionItem
              term="Thema's"
              description={getUnOrderedListItems(interventionDetail.interventieThema.thema)}
            />
            <DescriptionItem
              term="Settings"
              description={getUnOrderedListItems(interventionDetail.interventieSetting.setting)}
            />
            <DescriptionItem
              term="Doelgroepen"
              description={getUnOrderedListItems(interventionDetail.interventieDoelgroep.doelgroep)}
            />
            <DescriptionItem
              term="Leeftijd"
              description={getAgeText()}
            />
            <DescriptionItem
              term="Geslacht"
              description={interventionDetail.interventieLeeftijdGeslacht.geslacht.tekst}
            />
            <DescriptionItem
              term="Schaal en plaats"
              description={interventionDetail.interventieSchaal.locatieschaal.tekst}
            />
            <DescriptionItem
              term="Uitvoeringsstatus"
              description={getUnOrderedListItems(interventionDetail.interventieAlgemeen.uitvoeringStatus)}
            />
            <DescriptionItem
              term="Social media"
              description={getExternalLinks(interventionDetail.interventieAlgemeen.socialMedia)}
            />
            <DescriptionItem
              term="Website"
              description={getExternalLinks(interventionDetail.interventieAlgemeen.website)}
            />
          </dl>
        </div>
        <div className="c-intervention-description__group">
          <div className="c-intervention-description__header">
            <h3 className="c-intervention-description__title">Aanpak</h3>
          </div>
          <dl className="d-flex flex-wrap">
            <DescriptionItem
              term="Activiteiten en methodieken"
              description={getUnOrderedListItems(interventionDetail.interventieAanpak.activiteit)}
            />
            <DescriptionItem
              term="Maakt de interventie deel uit van een overkoepelend programma?"
              description={getYesNoText(interventionDetail.interventieAanpak.overkoepelendProgramma)}
            />
            <DescriptionItem
              term="Naam overkoepelend programma"
              description={interventionDetail.interventieAanpak.overkoepelendNaam}
            />
            <DescriptionItem
              term="Omschrijving overkoelepend programma"
              description={getRenderedHtml(interventionDetail.interventieAanpak.overkoepelendOmschrijving)}
            />
            <DescriptionItem
              term="Documenten"
              description={getInterventionDocuments(interventionDetail?.interventieAanpakDocument.bijlage)}
            />
          </dl>
        </div>
        <div className="c-intervention-description__group">
          <div className="c-intervention-description__header">
            <h3 className="c-intervention-description__title">Financiering</h3>
          </div>
          <dl className="d-flex flex-wrap">
            <DescriptionItem
              term="Ontwikkelkosten"
              description={interventionDetail?.interventieFinanciering.ontwikkelingsKosten.tekst}
            />
            <DescriptionItem
              term="Financiering ontwikkeling"
              description={getUnOrderedListItems(interventionDetail.interventieFinanciering.financieringOntwikkeling)}
            />
            <DescriptionItem
              term="Uitvoeringskosten"
              description={interventionDetail.interventieFinanciering.uitvoeringsKosten.tekst}
            />
            <DescriptionItem
              term="Financiering uitvoering"
              description={getUnOrderedListItems(interventionDetail.interventieFinanciering.financieringUitvoering)}
            />
          </dl>
        </div>
        <div className="c-intervention-description__group">
          <div className="c-intervention-description__header">
            <h3 className="c-intervention-description__title">Evaluatie</h3>
          </div>
          <dl className="d-flex flex-wrap">
            <DescriptionItem
              term="Specifieke succesfactoren"
              description={getRenderedHtml(interventionDetail?.interventieEvaluatie.succesfactoren)}
            />
            <DescriptionItem
              term="Specifieke aandachtspunten"
              description={getRenderedHtml(interventionDetail?.interventieEvaluatie.aandachtspunten)}
            />
            <DescriptionItem
              term="Toekomstige ontwikkeling"
              description={getRenderedHtml(interventionDetail?.interventieEvaluatie.toekomstigeOntwikkeling)}
            />
            <DescriptionItem
              term="Is de interventie geëvalueerd?"
              description={getRenderedHtml(interventionDetail.interventieEvaluatie.evaluatieVorm.tekst)}
            />
            <DescriptionItem
              term="Evaluatietype"
              description={getUnOrderedListItems(interventionDetail.interventieEvaluatie.evaluatieType)}
            />
            <DescriptionItem
              term="Documentatie"
              description={getInterventionDocuments(interventionDetail?.interventieEvaluatieDocument.bijlage)}
            />
          </dl>
        </div>
        <div className="c-intervention-description__group">
          <div className="c-intervention-description__header">
            <h3 className="c-intervention-description__title">Contact</h3>
          </div>
          <dl className="d-flex flex-wrap">
            <DescriptionItem
              term="Organisatie"
              description={interventionDetail.organisatie.naam}
            />
            <DescriptionItem
              term="Contactpersoon"
              description={<ContactList basicContactPerson={interventionDetail.interventieContactpersoon} />}
            />
          </dl>
        </div>
      </div>
    </div>
  );
};

export default ExpandedInterventionDetail;
